/**
 * BL-2318 — which permissions run each lot batch action: the ONE map the batch
 * write route enforces and the batch check reads to tell the status pill which
 * rows the viewer can run (BUG-1420's model — the check carries the viewer's
 * capability, so the menu reads one verdict). Pure (ADR 010 — Isomorphic Services).
 */
import { PERMISSIONS, type Permission } from '@auctogon/core-admin/api/permissions';
import { grantToPermission } from '@auctogon/core-admin/api/grants';
import type { BatchEditActionType } from '@auctogon/core-admin/schemas/lot-batch';

/**
 * Every move the batch check answers for: the batch actions, plus `reverse-sale`,
 * the fenced sold-lot exit the status pill launches (its own route, its own
 * grant `lot.correct_sale`), whose rows must disable for a role that can't run it
 * just like a batch move (BL-2318 RAC 5).
 */
export type BatchPermissionAction = BatchEditActionType | 'reverse-sale';

/**
 * The lot-write trio: every role that owns a lot-write surface — Catalogers
 * (INVENTORY_MANAGE, the /inventory bulk bar) and Admins (AUCTIONS_MANAGE +
 * CLERKING_MANAGE, sequencing bulk). BUG-1140.
 */
export const LOT_WRITE_PERMISSIONS: readonly Permission[] = [
  PERMISSIONS.INVENTORY_MANAGE,
  PERMISSIONS.AUCTIONS_MANAGE,
  PERMISSIONS.CLERKING_MANAGE,
];

/**
 * The permissions that may run a batch action (holding any one is enough).
 *
 * - `assign-to-auction` is a placement: AUCTIONS_MANAGE / CLERKING_MANAGE.
 * - `dispose-no-sale` (No Sale) is clerking: CLERKING_MANAGE alone (BL-1430).
 * - `reopen` matches the single-lot reopen route: INVOICES_MANAGE (BL-1533).
 * - `reverse-sale` is the reverse-sale route's own grant, `lot.correct_sale`
 *   (Admin and Accounting).
 * - `reassign-ring` with the ring-start push writes ring structure: Admin-tier
 *   (BUG-1593).
 * - everything else, `restore` and `move-to-auction` included, is a lot write:
 *   the trio (BUG-1140, BUG-1685, BL-1073).
 *
 * @param type - The batch action.
 * @param opts.pushRingStarts - Whether a ring reassignment also pushes ring starts.
 * @returns The permission set; the caller needs any one of them.
 */
export function batchActionPermissions(
  type: BatchPermissionAction,
  opts: { pushRingStarts?: boolean } = {},
): readonly Permission[] {
  if (type === 'assign-to-auction') return [PERMISSIONS.AUCTIONS_MANAGE, PERMISSIONS.CLERKING_MANAGE];
  if (type === 'dispose-no-sale') return [PERMISSIONS.CLERKING_MANAGE];
  if (type === 'reopen') return [PERMISSIONS.INVOICES_MANAGE];
  if (type === 'reverse-sale') return [grantToPermission('lot.correct_sale')];
  if (type === 'reassign-ring' && opts.pushRingStarts === true) {
    return [PERMISSIONS.AUCTIONS_MANAGE, PERMISSIONS.CLERKING_MANAGE];
  }
  return LOT_WRITE_PERMISSIONS;
}
